import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";

import { formatNumber } from "utils/format";
import Loading from "ui/Loading";
import Panel from "ui/Panel";
import Popover from "ui/Popover";
import { useRoutes } from "utils/routes";

import {
  OrganizationOverviewServerTableQuery,
  useOrganizationOverviewServerTableQuery,
} from "./gql/Query.generated";
import { ServerTagsTable } from "features/Dashboard/ServerDashboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTags } from "@fortawesome/pro-solid-svg-icons";
import { ServerStatusType, StatusDotIcon } from "ui/Icons";
import Tip from "ui/Tip";
import Grid from "ui/Grid";
import Badge from "ui/Badge";
import classNames from "classnames";
import { useQueryStringSearch } from "ui/QueryStringSearch";

type ServerStats =
  OrganizationOverviewServerTableQuery["getSummaryStatsByServer"];
type ServerStat = ServerStats[number];
type ServerTags = ServerStat["server"]["tags"];

type RowDataType = {
  serverId: string;
  serverName: string;
  tags: ServerTags;
  databaseCount: number;
  slowQueries: number;
  unusedIndexes: number;
  warnings: number;
  criticals: number;
  status: ServerStatusType;
  isPrimary: boolean | null;
};

type Props = {
  organizationSlug: string;
};

const Table: React.FunctionComponent<Props> = ({ organizationSlug }) => {
  const { data, loading, error } = useOrganizationOverviewServerTableQuery({
    variables: {
      organizationSlug,
    },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  return <TableContent data={data.getSummaryStatsByServer} />;
};

const TableContent = ({ data }: { data: ServerStats }) => {
  const popoverPortal = useRef();
  const [searchTerm, SearchInput] = useQueryStringSearch("q");
  const hasActiveServers = data.some((s) => s.server.hasRecentSnapshot);
  const [showInactive, setShowInactive] = useState(!hasActiveServers);

  const title = (
    <div>
      All Servers{" "}
      <Tip content="Servers that haven't sent data for more than 35 days will be automatically deleted" />
    </div>
  );
  const titleSearch = (
    <>
      <div className="flex">
        <input
          type="checkbox"
          checked={showInactive}
          id="show_inactive"
          onChange={(evt) => setShowInactive(evt.target.checked)}
        />
        <label htmlFor="show_inactive">
          Show servers with no recent statistics data
        </label>
      </div>
      <SearchInput />
    </>
  );

  let servers: Array<RowDataType> = data.map(
    (summary: ServerStat): RowDataType => {
      return {
        serverId: summary.server.humanId,
        serverName: summary.server.name,
        status:
          (summary.server.hasRecentSnapshot &&
            ((summary.server.hasCriticalIssues && "critical") || "okay")) ||
          "no data",
        tags: summary.server.tags,
        databaseCount: summary.databaseCount,
        slowQueries: summary.queriesSlownessCount,
        unusedIndexes: summary.unusedIndexesCount,
        warnings: summary.warningsCount,
        criticals: summary.criticalsCount,
        isPrimary: summary.server.latestStats?.isPrimary,
      };
    },
  );

  if (searchTerm) {
    servers = servers.filter((di: RowDataType): boolean => {
      const replica_status =
        di.isPrimary != null ? (di.isPrimary ? "primary" : "replica") : "";
      const match =
        di.serverName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        replica_status.includes(searchTerm.toLowerCase());
      return match;
    });
  }

  if (!showInactive) {
    servers = servers.filter((s) => s.status != "no data");
  }

  return (
    <Panel title={title} secondaryTitle={titleSearch}>
      <div ref={popoverPortal} />
      <Grid
        striped
        data={servers}
        defaultSortBy="serverName"
        columns={[
          {
            field: "status",
            header: "",
            width: "24px",
            renderer: function StatusCell({ fieldData }) {
              return <StatusDotIcon status={fieldData} />;
            },
          },
          {
            field: "serverName",
            header: "Server",
            width: "minmax(30%,1fr)",
            renderer: function ServernameCell({ rowData }) {
              return (
                <ServerNameCell rowData={rowData} portal={popoverPortal} />
              );
            },
          },
          {
            field: "databaseCount",
            header: "# of Databases",
            width: "minmax(10%,140px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function DBCountCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
          {
            field: "slowQueries",
            header: "Slow Queries",
            width: "minmax(10%,140px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function SlowQueryCountCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
          {
            field: "unusedIndexes",
            header: "Unused Indexes",
            width: "minmax(10%,140px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function UnusedIndexCountCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
          {
            field: "warnings",
            header: "Warnings",
            width: "minmax(10%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function WarningCountCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
          {
            field: "criticals",
            header: "Criticals",
            width: "minmax(10%,120px)",
            style: "number",
            defaultSortOrder: "DESC",
            nullValue: "n/a",
            renderer: function CriticalCountCell({ fieldData }) {
              return formatNumber(fieldData);
            },
          },
        ]}
      />
    </Panel>
  );
};

const ServerNameCell: React.FunctionComponent<{
  rowData: RowDataType;
  portal?: React.RefObject<HTMLDivElement>;
}> = ({ rowData, portal }) => {
  const { server } = useRoutes();
  const { tags, serverId } = rowData;
  return (
    <span className="flex items-center">
      <Link to={server(rowData.serverId)}>{rowData.serverName}</Link>
      {rowData.isPrimary != null && (
        <Badge
          className={classNames(
            "ml-[6px] border font-medium",
            !rowData.isPrimary && "!bg-[#f0f0f0] !text-[#555] !border-[#ddd]",
          )}
        >
          {rowData.isPrimary ? "primary" : "replica"}
        </Badge>
      )}
      {tags.length > 0 && (
        <Popover
          placement="bottom"
          wrapperClassName="ml-2 text-[12px]"
          content={<ServerTagsTable tags={tags} />}
          portal={portal?.current}
        >
          <Link to={server(serverId)}>
            <FontAwesomeIcon icon={faTags} />
          </Link>
        </Popover>
      )}
    </span>
  );
};

export default Table;
